'use client';

import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { SITE } from '@/app/constants';

const MEDIA_BASE =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset'
    : 'https://image01.cf.vidu.studio/vidu/media-asset';
const EXAMPLE_IMAGE_LEFT = `${MEDIA_BASE}/demo1-02ae3515.webp`;
const EXAMPLE_IMAGE_RIGHT = `${MEDIA_BASE}/demo2-a7cd19f3.webp`;

interface PhotoUploadTipsDialogProps {
  open: boolean;
  title: string;
  orLabel: string;
  tip1Title: string;
  tip1Description: string;
  tip2Title: string;
  tip2Description: string;
  tip3Title: string;
  tip3Description: string;
  confirmText: string;
  onConfirm: () => void;
  onClose: () => void;
}

function ExampleImage({ src }: { src: string }) {
  return (
    <div className="relative h-full min-w-0 flex-1 overflow-hidden rounded-lg">
      <img src={src} alt="" className="pointer-events-none size-full object-contain" />
    </div>
  );
}

export function PhotoUploadTipsDialog({
  open,
  title,
  orLabel,
  tip1Title,
  tip1Description,
  tip2Title,
  tip2Description,
  tip3Title,
  tip3Description,
  confirmText,
  onConfirm,
  onClose,
}: PhotoUploadTipsDialogProps) {
  const tips = [
    { title: tip1Title, description: tip1Description },
    { title: tip2Title, description: tip2Description },
    { title: tip3Title, description: tip3Description },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[calc(100dvh-32px)] w-[min(500px,calc(100%-32px))] max-w-none flex-col gap-0 overflow-hidden border-system-white08 bg-[#1c1c1c] p-0 text-white sm:max-h-[calc(100dvh-24px)] sm:w-[calc(100%-24px)]"
        closable
        onClose={onClose}
        closeClassName="top-6 right-6 text-white hover:text-white sm:top-5 sm:right-5"
        disableOverlayClose
      >
        <DialogHeader className="shrink-0 gap-0 space-y-0 px-6 pt-6 pr-14 pb-4 sm:px-5 sm:pt-5 sm:pr-12 sm:pb-3">
          <DialogTitle className="text-left font-medium text-white text-xl sm:text-lg">{title}</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 sm:px-5">
          <div className="flex h-[176px] items-stretch justify-center gap-2 sm:h-[128px] sm:gap-1.5">
            <ExampleImage src={EXAMPLE_IMAGE_LEFT} />
            <span className="flex shrink-0 items-center self-center font-medium text-[10px] text-white/30 uppercase leading-none">
              {orLabel}
            </span>
            <ExampleImage src={EXAMPLE_IMAGE_RIGHT} />
          </div>

          <div className="mt-5 space-y-3 sm:mt-4 sm:space-y-2.5">
            {tips.map((tip) => (
              <div key={tip.title}>
                <p className="font-medium text-base text-white leading-snug sm:text-sm">{tip.title}</p>
                <p className="mt-1 text-sm text-white/40 leading-snug sm:text-xs">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex shrink-0 justify-end px-6 pt-4 pb-6 sm:px-5 sm:pb-5">
          <Button variant="primary" size="dialog" className="min-w-[120px]" onClick={onConfirm}>
            {confirmText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
